import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Button,
  CircularProgress,
  Grid,
  LinearProgress,
  Paper,
  Typography,
  Chip,
  Avatar,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import TrendingDownIcon from '@mui/icons-material/TrendingDown';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import WarningIcon from '@mui/icons-material/Warning';
import ErrorIcon from '@mui/icons-material/Error';
import SpeedIcon from '@mui/icons-material/Speed';
import TimerIcon from '@mui/icons-material/Timer';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import WorkspacePremiumIcon from '@mui/icons-material/WorkspacePremium';
import axios from '../utils/api';
import theme from '../UserDashboard/theme';

const accent = theme.colors.brandDark;

const DEFAULT_PLAN_USAGE = {
  planName: 'Current Plan',
  user: '',
  robotId: '',
  usage: 0,
  limit: 0,
  remaining: 0,
  resetDate: null,
  status: 'ok',
  usedTimeFormatted: '0h 0m',
  remainingTimeFormatted: '0h 0m',
  usagePercentage: 0,
  planAddonStatus: '',
};

const NEAR_LIMIT_THRESHOLD = 80;

const toNumber = (value, fallback = 0) => {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : fallback;
};

const normalizeStatus = (status, usagePercent) => {
  const value = String(status || '').toLowerCase();
  if (value === 'ok' || value === 'warning' || value === 'exceeded') return value;
  if (usagePercent >= 100) return 'exceeded';
  if (usagePercent >= NEAR_LIMIT_THRESHOLD) return 'warning';
  return 'ok';
};

function pickPlanUsagePayload(raw, robotId) {
  const payloadArray = Array.isArray(raw?.data)
    ? raw.data
    : Array.isArray(raw)
      ? raw
      : null;
  if (!payloadArray?.length) return raw?.data || raw || {};
  if (robotId) {
    const match = payloadArray.find((r) => r?.robotId === robotId);
    if (match) return match;
  }
  return payloadArray[0];
}

const normalizePlanUsage = (raw, robotId) => {
  const payload = pickPlanUsagePayload(raw, robotId);

  const usage = toNumber(
    payload.usage ??
      payload.used ??
      payload.usedValue ??
      (payload.usedSeconds ? payload.usedSeconds / 3600 : 0),
    0,
  );
  const limit = toNumber(
    payload.limit ?? payload.max ?? payload.totalHoursPerMonth ?? payload.planLimit,
    0,
  );
  const remainingRaw =
    payload.remaining ??
    payload.remainingValue ??
    payload.remainingHours ??
    (payload.remainingSeconds ? payload.remainingSeconds / 3600 : null);
  const remaining = toNumber(
    remainingRaw,
    limit > 0 ? Math.max(limit - usage, 0) : DEFAULT_PLAN_USAGE.remaining,
  );
  const usagePercent = toNumber(
    payload.usagePercentage,
    limit > 0 ? Math.min((usage / limit) * 100, 100) : 0,
  );

  return {
    planName: payload.planName || payload.plan || DEFAULT_PLAN_USAGE.planName,
    user: payload.user || '',
    robotId: payload.robotId || '',
    usage,
    limit,
    remaining: Math.max(remaining, 0),
    resetDate: payload.resetDate || payload.renewalDate || null,
    status: normalizeStatus(payload.status, usagePercent),
    usedTimeFormatted:
      payload.usedTimeFormatted || `${toNumber(usage).toFixed(2)} hrs`,
    remainingTimeFormatted:
      payload.remainingTimeFormatted || `${toNumber(remaining).toFixed(2)} hrs`,
    usagePercentage: Math.min(Math.max(usagePercent, 0), 100),
    planAddonStatus: payload.planAddonStatus || '',
  };
};

function getAlertMeta(status) {
  switch (status) {
    case 'exceeded':
      return {
        icon: <ErrorIcon sx={{ fontSize: 22 }} />,
        title: 'Limit Exceeded',
        message: 'You have reached your plan limit. Upgrade or contact support to continue usage.',
        gradient: 'linear-gradient(135deg, #ef4444 0%, #dc2626 100%)',
        bgGradient: 'linear-gradient(135deg, #fef2f2 0%, #fee2e2 100%)',
        color: '#dc2626',
      };
    case 'warning':
      return {
        icon: <WarningIcon sx={{ fontSize: 22 }} />,
        title: 'Near Usage Limit',
        message: 'You are close to your monthly limit. Plan an upgrade to avoid interruptions.',
        gradient: 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)',
        bgGradient: 'linear-gradient(135deg, #fffbeb 0%, #fef3c7 100%)',
        color: '#d97706',
      };
    default:
      return {
        icon: <CheckCircleIcon sx={{ fontSize: 22 }} />,
        title: 'Usage Healthy',
        message: 'Usage is within your current plan limits.',
        gradient: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
        bgGradient: 'linear-gradient(135deg, #ecfdf5 0%, #d1fae5 100%)',
        color: '#059669',
      };
  }
}

function UsageRing({ percent, label, color, gradient }) {
  const p = Math.max(0, Math.min(100, percent));
  const circumference = 2 * Math.PI * 45;
  const offset = circumference - (p / 100) * circumference;
  
  return (
    <Box sx={styles.ringContainer}>
      <Box sx={{ position: 'relative', display: 'inline-flex' }}>
        <Box sx={{ position: 'relative', width: 160, height: 160 }}>
          <svg width="160" height="160" viewBox="0 0 120 120">
            <circle
              cx="60"
              cy="60"
              r="52"
              fill="none"
              stroke="#e2e8f0"
              strokeWidth="8"
            />
            <circle
              cx="60"
              cy="60"
              r="52"
              fill="none"
              stroke={color}
              strokeWidth="8"
              strokeDasharray={circumference}
              strokeDashoffset={offset}
              strokeLinecap="round"
              style={{ transition: 'stroke-dashoffset 0.5s ease' }}
              transform="rotate(-90 60 60)"
            />
          </svg>
          <Box sx={styles.ringInner}>
            <Typography sx={styles.ringValue}>{p}%</Typography>
            <Typography sx={styles.ringLabel}>{label}</Typography>
          </Box>
        </Box>
      </Box>
    </Box>
  );
}

function MetricCard({ label, value, icon: Icon, color, subtitle }) {
  return (
    <Box
      sx={{
        ...styles.metricCard,
        '&:hover': {
          transform: 'translateY(-4px)',
          boxShadow: '0 12px 24px rgba(0,0,0,0.1)',
        },
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between' }}>
        <Box>
          <Typography sx={styles.metricLabel}>{label}</Typography>
          <Typography sx={styles.metricValue}>{value}</Typography>
          {subtitle && <Typography sx={styles.metricSubtitle}>{subtitle}</Typography>}
        </Box>
        <Avatar sx={{ bgcolor: `${color}15`, color: color, width: 48, height: 48 }}>
          <Icon />
        </Avatar>
      </Box>
    </Box>
  );
}

function PlanUsageDashboard({ robotId, onPlanNameChange, embedded = false, showBack = false }) {
  const navigate = useNavigate();
  const [planUsage, setPlanUsage] = useState({
    loading: true,
    error: null,
    data: DEFAULT_PLAN_USAGE,
  });
  const fetchUserPlanUsage = async () => {
    setPlanUsage((prev) => ({ ...prev, loading: true, error: null }));

    try {
      const response = await axios.get('/user/plan-usage');
      const normalizedData = normalizePlanUsage(response.data, robotId);
      setPlanUsage({ loading: false, error: null, data: normalizedData });
      onPlanNameChange?.(normalizedData.planName || null);
    } catch (error) {
      setPlanUsage((prev) => ({
        loading: false,
        error:
          error.response?.data?.message ||
          'Unable to load plan usage right now. Please try again.',
        data: prev.data || DEFAULT_PLAN_USAGE,
      }));
      onPlanNameChange?.(null);
    }
  };

  useEffect(() => {
    fetchUserPlanUsage();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [robotId]);

  const usage = planUsage.data || DEFAULT_PLAN_USAGE;
  const usedPercent = Math.max(
    0,
    Math.min(
      100,
      Math.round(
        toNumber(
          usage.usagePercentage,
          usage.limit > 0 ? Math.min((usage.usage / usage.limit) * 100, 100) : 0,
        ),
      ),
    ),
  );
  const remainingPercent = Math.max(100 - usedPercent, 0);
  const alert = getAlertMeta(usage.status);
  const resetDateText = usage.resetDate
    ? new Date(usage.resetDate).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })
    : 'Not available';
  const displayRobotId = usage.robotId || robotId;

  if (planUsage.loading) {
    return (
      <Box sx={embedded ? styles.embedWrap : styles.pageWrap}>
        <Box sx={styles.loaderBox}>
          <div className="pulse-animation">
            <CircularProgress size={56} sx={{ color: theme.colors.brandPrimary, mb: 2 }} />
          </div>
          <Typography sx={{ fontSize: 14, color: theme.colors.textSecondary }}>
            Loading usage dashboard...
          </Typography>
        </Box>
      </Box>
    );
  }

  const getProgressColor = () => {
    if (usedPercent >= 100) return '#ef4444';
    if (usedPercent >= 80) return '#f59e0b';
    return '#10b981';
  };

  return (
    <Box sx={embedded ? styles.embedWrap : styles.pageWrap}>
      <Box sx={styles.shell}>
        {showBack && (
          <Button
            startIcon={<ArrowBackIcon />}
            onClick={() => navigate(-1)}
            sx={styles.backBtn}
          >
            Back to Dashboard
          </Button>
        )}

        <Box sx={{ mb: 3 }}>
          <Typography sx={styles.pageTitle}>
            {embedded ? 'Plan Usage Analytics' : 'Usage Intelligence Dashboard'}
          </Typography>
          <Typography sx={styles.pageSubtitle}>
            Monitor your consumption patterns and stay within limits
          </Typography>

          <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'center', flexWrap: 'wrap', mt: 2 }}>
            <Chip
              icon={<WorkspacePremiumIcon />}
              label={usage.planName}
              sx={{
                bgcolor: 'white',
                border: `1px solid ${theme.colors.borderDefault}`,
                fontWeight: 600,
                fontSize: 13,
                '& .MuiChip-icon': { color: accent },
              }}
            />
            {displayRobotId && (
              <Chip
                label={`Robot: ${displayRobotId}`}
                sx={{
                  bgcolor: '#f1f5f9',
                  fontSize: 12,
                  fontWeight: 500,
                }}
              />
            )}
            <Chip
              label={usage.status === 'ok' ? 'Active' : usage.status === 'warning' ? 'Near Limit' : 'Exceeded'}
              sx={{
                bgcolor: alert.bgGradient,
                color: alert.color,
                fontWeight: 600,
                fontSize: 12,
              }}
            />
          </Box>
        </Box>

        <Grid container spacing={3}>
          <Grid item xs={12} md={6}>
            <Paper elevation={0} sx={styles.panel}>
              <Typography sx={styles.panelTitle}>
                <SpeedIcon sx={{ fontSize: 18, mr: 1, verticalAlign: 'middle' }} />
                Usage Overview
              </Typography>
              <Box sx={styles.ringsRow}>
                <UsageRing percent={usedPercent} label="Used" color="#ef4444" />
                <UsageRing percent={remainingPercent} label="Remaining" color="#22c55e" />
              </Box>
            </Paper>
          </Grid>
          <Grid item xs={12} md={6}>
            <Paper elevation={0} sx={styles.panel}>
              <Typography sx={styles.panelTitle}>
                <TimerIcon sx={{ fontSize: 18, mr: 1, verticalAlign: 'middle' }} />
                Key Metrics
              </Typography>
              <Grid container spacing={2}>
                <Grid item xs={6}>
                  <MetricCard
                    label="Total Hours Used"
                    value={`${Math.round(usage.usage * 10) / 10}h`}
                    icon={TrendingUpIcon}
                    color="#ef4444"
                    subtitle={`of ${Math.round(usage.limit)}h limit`}
                  />
                </Grid>
                <Grid item xs={6}>
                  <MetricCard
                    label="Hours Remaining"
                    value={`${Math.round(usage.remaining * 10) / 10}h`}
                    icon={TrendingDownIcon}
                    color="#10b981"
                  />
                </Grid>
                <Grid item xs={6}>
                  <MetricCard
                    label="Reset Date"
                    value={resetDateText}
                    icon={CalendarTodayIcon}
                    color="#3b82f6"
                  />
                </Grid>
                {usage.planAddonStatus && (
                  <Grid item xs={6}>
                    <MetricCard
                      label="Add-on Status"
                      value={usage.planAddonStatus}
                      icon={WorkspacePremiumIcon}
                      color="#8b5cf6"
                    />
                  </Grid>
                )}
              </Grid>
            </Paper>
          </Grid>
        </Grid>

        <Paper elevation={0} sx={styles.progressPanel}>
          <Box sx={styles.progressHeader}>
            <Typography sx={{ fontWeight: 600, color: '#1a1a2e' }}>
              Monthly Consumption
            </Typography>
            <Box sx={{ display: 'flex', gap: 2 }}>
              <Typography sx={{ fontSize: 13, color: '#6c6c8a' }}>
                Used: <strong style={{ color: '#ef4444' }}>{usedPercent}%</strong>
              </Typography>
              <Typography sx={{ fontSize: 13, color: '#6c6c8a' }}>
                Remaining: <strong style={{ color: '#22c55e' }}>{remainingPercent}%</strong>
              </Typography>
            </Box>
          </Box>
          <Box sx={{ position: 'relative' }}>
            <LinearProgress
              variant="determinate"
              value={Math.min(usedPercent, 100)}
              sx={{
                height: 12,
                borderRadius: 6,
                bgcolor: '#e2e8f0',
                '& .MuiLinearProgress-bar': {
                  borderRadius: 6,
                  background: `linear-gradient(90deg, ${getProgressColor()}CC, ${getProgressColor()})`,
                },
              }}
            />
          </Box>
        </Paper>

        <Paper elevation={0} sx={{ ...styles.alertPanel, background: alert.bgGradient }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <Box sx={{ color: alert.color }}>{alert.icon}</Box>
            <Box sx={{ flex: 1 }}>
              <Typography sx={{ fontWeight: 700, fontSize: 15, color: alert.color }}>
                {alert.title}
              </Typography>
              <Typography sx={{ fontSize: 13, color: alert.color, opacity: 0.9 }}>
                {alert.message}
              </Typography>
            </Box>
          </Box>
        </Paper>

        {planUsage.error && (
          <Paper elevation={0} sx={styles.errorBox}>
            <Typography sx={{ fontSize: 14, color: '#991b1b' }}>{planUsage.error}</Typography>
          </Paper>
        )}

        <style>
          {`
            .pulse-animation {
              animation: pulse 1.5s ease-in-out infinite;
            }
            @keyframes pulse {
              0%, 100% { opacity: 1; transform: scale(1); }
              50% { opacity: 0.7; transform: scale(0.98); }
            }
          `}
        </style>
      </Box>
    </Box>
  );
}

/** Embedded in RobotDetailView */
export function PlanUsageView({ robotId, onPlanNameChange }) {
  return (
    <PlanUsageDashboard
      robotId={robotId}
      onPlanNameChange={onPlanNameChange}
      embedded
    />
  );
}

/** Standalone plan usage page */
export default function PlanUsage() {
  return <PlanUsageDashboard showBack />;
}

const styles = {
  pageWrap: {
    position: 'relative',
    minHeight: '100vh',
    py: { xs: 2, md: 4 },
    px: { xs: 2, md: 3 },
    background: 'linear-gradient(135deg, #f0f4f8 0%, #e9edf2 100%)',
  },
  embedWrap: {
    py: 0,
    px: 0,
    minHeight: 'auto',
    background: 'transparent',
  },
  shell: {
    maxWidth: 1100,
    mx: 'auto',
  },
  loaderBox: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    minHeight: 400,
    p: 4,
    borderRadius: '28px',
    bgcolor: 'white',
    border: '1px solid rgba(0,0,0,0.05)',
    boxShadow: '0 20px 35px -12px rgba(0,0,0,0.1)',
    maxWidth: 420,
    mx: 'auto',
  },
  backBtn: {
    mb: 2.5,
    textTransform: 'none',
    fontWeight: 600,
    borderRadius: '12px',
    px: 2,
    py: 0.75,
    color: '#1a1a2e',
    borderColor: '#e2e8f0',
    bgcolor: 'white',
    '&:hover': {
      bgcolor: '#f8fafc',
      borderColor: '#cbd5e1',
    },
  },
  pageTitle: {
    fontSize: { xs: '1.75rem', md: '2.25rem' },
    fontWeight: 800,
    background: 'linear-gradient(135deg, #1a1a2e 0%, #16213e 100%)',
    backgroundClip: 'text',
    WebkitBackgroundClip: 'text',
    color: 'transparent',
    letterSpacing: '-0.02em',
    mb: 0.5,
  },
  pageSubtitle: {
    fontSize: 14,
    color: '#6c6c8a',
  },
  panel: {
    p: { xs: 2.5, md: 3 },
    borderRadius: '24px',
    border: '1px solid rgba(0,0,0,0.05)',
    bgcolor: 'white',
    boxShadow: '0 8px 20px rgba(0,0,0,0.04)',
    height: '100%',
    transition: 'all 0.2s ease',
    '&:hover': {
      boxShadow: '0 12px 28px rgba(0,0,0,0.08)',
    },
  },
  panelTitle: {
    fontSize: 16,
    fontWeight: 700,
    color: '#1a1a2e',
    mb: 2.5,
    display: 'flex',
    alignItems: 'center',
  },
  ringsRow: {
    display: 'flex',
    flexWrap: 'wrap',
    justifyContent: 'center',
    gap: 3,
  },
  ringContainer: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
  },
  ringInner: {
    position: 'absolute',
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
    textAlign: 'center',
  },
  ringValue: {
    fontSize: '1.5rem',
    fontWeight: 800,
    color: '#1a1a2e',
    lineHeight: 1.2,
  },
  ringLabel: {
    fontSize: '0.75rem',
    color: '#6c6c8a',
    fontWeight: 500,
  },
  metricCard: {
    p: 1.5,
    borderRadius: '16px',
    border: '1px solid #e2e8f0',
    bgcolor: '#f8fafc',
    transition: 'all 0.2s ease',
    cursor: 'pointer',
  },
  metricLabel: {
    fontSize: 11,
    fontWeight: 600,
    color: '#6c6c8a',
    textTransform: 'uppercase',
    letterSpacing: '0.03em',
    mb: 0.5,
  },
  metricValue: {
    fontSize: 20,
    fontWeight: 800,
    color: '#1a1a2e',
  },
  metricSubtitle: {
    fontSize: 10,
    color: '#94a3b8',
    mt: 0.25,
  },
  progressPanel: {
    mt: 3,
    p: { xs: 2, md: 2.5 },
    borderRadius: '20px',
    border: '1px solid rgba(0,0,0,0.05)',
    bgcolor: 'white',
    boxShadow: '0 4px 12px rgba(0,0,0,0.04)',
  },
  progressHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    mb: 1.5,
    flexWrap: 'wrap',
    gap: 1,
  },
  alertPanel: {
    mt: 2.5,
    p: 2.5,
    borderRadius: '20px',
    border: '1px solid rgba(0,0,0,0.05)',
    transition: 'all 0.2s ease',
  },
  errorBox: {
    mt: 2,
    p: 2,
    borderRadius: '16px',
    bgcolor: '#fef2f2',
    border: '1px solid #fecaca',
  },
};